import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { TokenService } from '../services/token.service';
import { TokenAtualizacaoService } from '../services/token-atualizacao.service';

function estaAutenticado(): boolean {
    const token = inject(TokenService);
    return token.estaLogadoValido();
}

/**
 * Access token expira em minutos, mas o refresh token (cookie httpOnly) dura dias.
 * Antes de declarar o usuário deslogado, tenta renovar a sessão via refresh -
 * senão qualquer navegação de rota após o access token expirar derruba a sessão
 * mesmo com refresh token ainda válido.
 */
async function garantirSessaoValida(): Promise<boolean> {
    const token = inject(TokenService);
    if (token.estaLogadoValido()) return true;
    if (!token.buscarToken()) return false;

    const refresh = inject(TokenAtualizacaoService);
    return refresh.tentarAtualizarToken();
}

/**
 * Guards que redirecionam devolvendo UrlTree precisam ser `canActivate`, e não `canMatch`.
 * `canMatch` roda durante o casamento da rota: a rota de path vazio ('') é testada contra
 * qualquer URL, então o guard dispararia o redirecionamento até em /autenticacao/login,
 * criando um loop infinito de navegação.
 */

/** Guard para áreas autenticadas do Hub */
export const authGuard: CanActivateFn = async (_route, state) => {
    const router = inject(Router);
    if (await garantirSessaoValida()) return true;

    const tree: UrlTree = router.createUrlTree(['/autenticacao/login'], { queryParams: { returnUrl: state.url } });
    return tree;
};

/**
 * Guard para páginas públicas de login/registro - redireciona se já estiver logado.
 * Se a navegação veio de um deep-link externo (?modulo=), redireciona pro handoff
 * em vez do Hub, pra devolver o usuário direto pro módulo de origem.
 */
export const visitanteGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
    const router = inject(Router);
    if (!estaAutenticado()) return true;

    const modulo = route.queryParamMap.get('modulo');
    if (modulo) {
        const returnTo = route.queryParamMap.get('returnTo');
        return router.createUrlTree(['/autenticacao/handoff'], { queryParams: { modulo, returnTo } });
    }

    return router.createUrlTree(['/']);
};

/** Guard baseado no papel do usuário no Hub (não no papel de um módulo específico) */
export function hubRoleGuard(papeisPermitidos: string[]): CanActivateFn {
    return async (_route, state) => {
        const router = inject(Router);
        const token = inject(TokenService);

        if (!(await garantirSessaoValida()))
            return router.createUrlTree(['/autenticacao/login'], { queryParams: { returnUrl: state.url } });

        const papel = token.obterHubRole() ?? '';
        return papeisPermitidos.includes(papel) ? true : router.createUrlTree(['/nao-autorizado']);
    };
}
